import { useCallback } from "react";

import type { BillingStatusUpdateInput } from "@/lib/gql/__generated__/graphql";

import * as HotelApplicationDataRepository from "@/components/features/_shared/repository/hotel-application-data";

import type * as FormModelType from "../type";

/**
 * alternativeSystemProductNameとalternativeSystemReasonが存在する場合、
 * commentの末尾に"製品名：~~~, 理由：~~~"の形式でマージする
 */
const mergeAlternativeSystemInfoToComment = (
	inputModel: FormModelType.CancellationFormModel["InputModel"],
): BillingStatusUpdateInput => {
	const { alternativeSystemProductName, alternativeSystemReason, comment, ...rest } = inputModel.churnInfo;

	let mergedComment = "";

	// alternativeSystemProductNameとalternativeSystemReasonが両方存在する場合のみマージ
	if (alternativeSystemProductName && alternativeSystemReason) {
		const additionalInfo = `製品名：${alternativeSystemProductName}\n乗り換え理由：${alternativeSystemReason}`;
		mergedComment = mergedComment ? `${mergedComment}\n\n${additionalInfo}` : additionalInfo;
	}

	// commentが存在する場合は"質問や要望："のprefixをつける
	if (comment) {
		const additionalInfo = `質問や要望：${comment}`;
		mergedComment = mergedComment ? `${mergedComment}\n\n${additionalInfo}` : additionalInfo;
	}

	return {
		hotelID: inputModel.hotelID,
		defaultBasicBillingTicketID: inputModel.defaultBasicBillingTicketID,
		defaultProBillingTicketID: inputModel.defaultProBillingTicketID,
		isStopRecurringBasicBilling: inputModel.isStopRecurringBasicBilling,
		isStopRecurringProBilling: inputModel.isStopRecurringProBilling,
		churnInfo: {
			...rest,
			comment: mergedComment || null,
		},
	};
};

export const useCancellation = (inputModel: FormModelType.CancellationFormModel["InputModel"]) => {
	const { onUpdateHotelApplicationData } = HotelApplicationDataRepository.Mutation.useHotelApplicationDataMutation();

	const onCancellation = useCallback(() => {
		const mergedInput = mergeAlternativeSystemInfoToComment(inputModel);
		return onUpdateHotelApplicationData(mergedInput);
	}, [inputModel, onUpdateHotelApplicationData]);

	return {
		onCancellation,
	};
};
